import { responseType } from '../../../common/type/ResponseType';
import { UserInfoType } from '../../../common/type/UserInfoType';
import { removeLocalStorage, setLocalStorage } from '../../../common/utils/localStorageHandler';
import { setSessionStorage } from '../../../common/utils/sessionStorageHandler';
import { nonNullable } from '../../../common/utils/nonNullable';
import {
	localStorageKeyCompanyCode,
	localStorageKeyUserCode,
	permission,
	sessionStorageKeyAuthorizationToken,
	sessionStorageKeyTokenIssuedDatetime,
	unknownLabel,
} from '../../../common/const/commonConst';
import { determinePermission } from '../../../common/utils/determinePermission';

import { LoginInput } from '../../../API';
import { queryLogin } from '../api/queryLogin';
import { OffsegAuthInfoType } from '../type/AuthInfoType';
import { LoginResponseType } from '../type/LoginResponseType';
import { createAuthorizationTokenString } from '../../../common/utils/createTokenString';
import { objectNowDate } from '../../../common/utils/dateHandler';
import { WebStorageError } from '../../../common/utils/WebStorageError';

export const loginHandler = async (
	offsegAuthInfo: OffsegAuthInfoType,
	isChecked: boolean,
	isReregistrationDialogStep1Opened: boolean,
	setUserInfo: React.Dispatch<React.SetStateAction<UserInfoType>>,
): Promise<LoginResponseType> => {
	// 法人コードのバリデーションチェック（空文字、最大桁数、半角英数字記号）
	if (!offsegAuthInfo.companyCode || [...offsegAuthInfo.companyCode].length >= 256 || /[^a-zA-Z0-9-_]/.test(offsegAuthInfo.companyCode)) {
		console.error('validation error');
		return [responseType.validationError, permission.id.unknown];
	}
	// ユーザIDのバリデーションチェック（空文字、最大桁数、半角英数字記号）
	if (!offsegAuthInfo.userCode || [...offsegAuthInfo.userCode].length >= 256 || /[^\x21-\x7e]/.test(offsegAuthInfo.userCode)) {
		console.error('validation error');
		return [responseType.validationError, permission.id.unknown];
	}
	// パスワードのバリデーションチェック（空文字、最大桁数、半角英数字記号）
	if (
		!offsegAuthInfo.password ||
		[...offsegAuthInfo.password].length <= 9 ||
		[...offsegAuthInfo.password].length >= 65 ||
		/[^a-zA-Z0-9^$*.[\]{}()?"!@#%&/\\,><':;|_~`]/.test(offsegAuthInfo.password)
	) {
		console.error('validation error');
		return [responseType.validationError, permission.id.unknown];
	}

	try {
		// 認証トークンを作成
		const authorizationToken = createAuthorizationTokenString();

		const loginInput: LoginInput = {
			companyCode: offsegAuthInfo.companyCode,
			userCode: offsegAuthInfo.userCode,
			password: offsegAuthInfo.password,
			authorizationToken: authorizationToken,
			reregistrationFlag: isReregistrationDialogStep1Opened,
		};

		// ログインAPIを実行
		const [isApiError, loginResult] = await queryLogin(loginInput, authorizationToken);

		if (isApiError || !loginResult) {
			return [responseType.systemError, permission.id.unknown];
		}

		switch (loginResult.login.responseType) {
			case responseType.unregisteredAccount:
				// SessionStorageに認証トークンを格納
				setSessionStorage(sessionStorageKeyAuthorizationToken, authorizationToken);
				setSessionStorage(sessionStorageKeyTokenIssuedDatetime, objectNowDate().toString());
				return [responseType.unregisteredAccount, permission.id.unknown];

			case responseType.reqeustChangeInitialPassword:
				return [responseType.reqeustChangeInitialPassword, permission.id.unknown];

			case responseType.reregistrationStep1Success:
				// SessionStorageに認証トークンを格納
				setSessionStorage(sessionStorageKeyAuthorizationToken, authorizationToken);
				setSessionStorage(sessionStorageKeyTokenIssuedDatetime, objectNowDate().toString());
				return [responseType.reregistrationStep1Success, permission.id.unknown];

			case responseType.notExistApiResponseData:
				return [responseType.notExistApiResponseData, permission.id.unknown];

			case responseType.loginLockError:
				return [responseType.loginLockError, permission.id.unknown];

			case responseType.apiError:
				return [responseType.apiError, permission.id.unknown];

			case responseType.dbError:
				return [responseType.dbError, permission.id.unknown];

			case responseType.systemError:
				return [responseType.systemError, permission.id.unknown];
		}

		const loginUserInfo = loginResult.login.loginUserInfo.filter(nonNullable)[0];

		// 共通ウェブポータルサービスの権限を判定
		const [isError, permissionNum] = determinePermission(loginUserInfo.permissionName, loginUserInfo.authority);

		// エラーチェック
		if (isError) {
			return [responseType.systemError, permission.id.unknown];
		}

		if (isChecked) {
			setLocalStorage(localStorageKeyCompanyCode, offsegAuthInfo.companyCode);
			setLocalStorage(localStorageKeyUserCode, offsegAuthInfo.userCode);
		} else {
			removeLocalStorage(localStorageKeyCompanyCode);
			removeLocalStorage(localStorageKeyUserCode);
		}

		// SessionStorageに認証トークンを格納
		setSessionStorage(sessionStorageKeyAuthorizationToken, authorizationToken);
		setSessionStorage(sessionStorageKeyTokenIssuedDatetime, objectNowDate().toString());

		const userInfo = {
			isLogin: true,
			companyName: loginUserInfo.companyName || unknownLabel,
			userName: loginUserInfo.userName || unknownLabel,
			permission: permissionNum,
		};

		setUserInfo(userInfo);

		return [responseType.success, permissionNum];
	} catch (error) {
		if (error instanceof WebStorageError) {
			return [responseType.WebStorageError, permission.id.unknown];
		}
		console.error(error);
		return [responseType.systemError, permission.id.unknown];
	}
};
